'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { articles } from '@/data/insights';
import { createClient } from '@/lib/supabase/client';

interface InsightsPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  date: string | null;
  author: string;
  read_time: string | null;
  published: boolean;
}

const categories = ['All', 'Bottlenecks & Revenue Leaks', 'Execution Systems', 'AI + Automation for Operators', 'Follow-Up, Retention & Conversion', 'Founder Operating Frameworks', 'Build vs Retainer vs Sprint'];

function NewsletterCapture() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({
          email: email.trim().toLowerCase(),
          source: 'insights_inline',
          subscribed_at: new Date().toISOString(),
        });
      if (error) {
        if (error.code === '23505') {
          setStatus('success');
        } else {
          throw error;
        }
      } else {
        setStatus('success');
      }
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="border border-primary/40 bg-surface-1 p-8 md:p-10 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-primary" />
      {status === 'success' ? (
        <div className="text-center py-4">
          <div className="w-12 h-12 bg-primary/10 border border-primary flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">You&apos;re in.</h3>
          <p className="text-muted-foreground text-sm">Expect sharp, no-fluff insights delivered straight to your inbox.</p>
        </div>
      ) : (
        <>
          <div className="text-micro uppercase tracking-widest text-primary mb-3">Newsletter</div>
          <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2">Get Our Latest Insights</h3>
          <p className="text-muted-foreground text-sm md:text-base mb-6 max-w-lg">
            Strategy, frameworks, and growth playbooks delivered to your inbox. No fluff — just the thinking behind how we operate.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={status === 'loading'}
              className="flex-1 px-4 py-3 bg-background border border-border text-white placeholder-muted-foreground text-sm focus:outline-none focus:border-primary transition-colors disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={status === 'loading' || !email.trim()}
              className="px-6 py-3 bg-primary text-white font-semibold text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
          {status === 'error' && <p className="mt-3 text-xs text-red-400">{errorMsg}</p>}
          <p className="mt-3 text-xs text-muted-foreground">No spam. Unsubscribe anytime.</p>
        </>
      )}
    </div>
  );
}

function ArticleCard({ article }: { article: InsightsPost }) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <Link href={`/insights/${article.slug}`} className="group block">
      <div className="border border-border hover:border-primary transition-all duration-300 bg-surface-1 h-full flex flex-col">
        <div className="p-8 flex-1 flex flex-col">
          <div className="mb-5">
            <Badge variant={article.category === 'Framework' ? 'primary' : 'default'} className="text-xs mb-3">
              {article.category}
            </Badge>
            <div className="text-micro uppercase tracking-widest text-muted-foreground">
              {formatDate(article.date)}
            </div>
          </div>
          <h2 className="text-xl font-bold text-white leading-tight mb-4 group-hover:text-primary transition-colors">
            {article.title}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
              <span className="text-label uppercase tracking-widest">Read Article</span>
              <span className="text-lg">→</span>
            </div>
            <span className="text-micro text-muted-foreground">{article.read_time}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function InsightsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [posts, setPosts] = useState<InsightsPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('insights_posts')
          .select('id, slug, title, excerpt, category, date, author, read_time, published')
          .eq('published', true)
          .order('date', { ascending: false });

        if (error || !data || data.length === 0) {
          // Fallback to static data
          const fallback: InsightsPost[] = articles.map(a => ({
            id: a.slug,
            slug: a.slug,
            title: a.title,
            excerpt: a.excerpt,
            category: a.category,
            date: a.dateISO,
            author: a.author,
            read_time: a.readTime,
            published: true,
          }));
          setPosts(fallback);
        } else {
          setPosts(data);
        }
      } catch {
        // Fallback to static data on error
        const fallback: InsightsPost[] = articles.map(a => ({
          id: a.slug,
          slug: a.slug,
          title: a.title,
          excerpt: a.excerpt,
          category: a.category,
          date: a.dateISO,
          author: a.author,
          read_time: a.readTime,
          published: true,
        }));
        setPosts(fallback);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filtered = activeCategory === 'All'
    ? posts
    : posts.filter(p => p.category === activeCategory);

  const firstBatch = filtered.slice(0, 2);
  const restBatch = filtered.slice(2);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-40 pb-24 px-6 border-b border-border">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-3xl">
              <div className="text-micro uppercase tracking-widest text-primary mb-6">Insights</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] text-white mb-6">
                Strategic content for founders who execute
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Bottleneck diagnosis, execution systems, AI leverage, and sprint-first operating frameworks — written for founder-led service businesses doing $500K–$5M. No fluff. No generic agency advice.
              </p>
            </div>
          </div>
        </section>

        {/* Filter + Articles */}
        <section className="py-20 px-6">
          <div className="max-w-[1200px] mx-auto">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 mb-16">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 text-label uppercase tracking-widest border transition-all duration-200 ${
                    activeCategory === cat
                      ? 'border-primary text-primary bg-primary/10' :'border-border text-muted-foreground hover:border-white hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="border border-border bg-surface-1 h-64 animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-muted-foreground">No articles in this category yet.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {firstBatch.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {firstBatch.map(article => (
                      <ArticleCard key={article.slug} article={article} />
                    ))}
                  </div>
                )}
                <NewsletterCapture />
                {restBatch.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {restBatch.map(article => (
                      <ArticleCard key={article.slug} article={article} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-6 border-t border-border bg-surface-1">
          <div className="max-w-[800px] mx-auto text-center">
            <div className="text-micro uppercase tracking-widest text-primary mb-4">Work With Us</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-white mb-6">
              Ready to put these ideas into practice?
            </h2>
            <p className="text-muted-foreground mb-10 text-lg">
              Every engagement starts with a Sprint — a paid diagnostic that maps your constraints and delivers a clear execution roadmap. No pressure to commit before we understand your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/book">
                <Button variant="primary" size="lg">Book a Sprint — Start With a Diagnostic</Button>
              </Link>
              <Link href="/case-studies">
                <Button variant="outline" size="lg">View Proof & Process</Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}